// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Contract Analysis Layout - renders aggregated contract analysis data.
 * Receives all data via props (no fetching). Parent aggregates from multiple datasets.
 */
import React from 'react'
import { CheckCircle2 } from 'lucide-react'
import { cn } from '@/components/ui'
import { SummaryCard } from './summary/SummaryCard'
import { FindingsList } from './findings/components/FindingsList'
import type { ContractFinding } from '../types'

export interface ContractAnalysisLayoutProps {
  headline?: string
  summary?: string
  score: number | null
  findings: ContractFinding[]
  className?: string
  documentId?: string
  orgId?: string
  workspaceId?: string
  agentId?: string
  datasetKey?: string
  version?: number
  /**
   * Run these findings came from. The redline annotates exactly these findings,
   * so its artifacts have to be read from this run rather than from whichever run
   * at this version is newest.
   */
  run?: number
  documentName?: string
}

export const ContractAnalysisLayout: React.FC<ContractAnalysisLayoutProps> = ({
  headline,
  summary,
  score,
  findings,
  className,
  documentId,
  orgId,
  workspaceId,
  agentId,
  datasetKey,
  version,
  run,
  documentName,
}) => {
  // Why: preserve original index for redline API while sorting for display
  const findingsWithIndex = findings.map((f, i) => ({ ...f, originalIndex: i }))
  const sortedFindings = [...findingsWithIndex].sort((a, b) => {
    const pageA = a.pageNumber ?? Number.MAX_SAFE_INTEGER
    const pageB = b.pageNumber ?? Number.MAX_SAFE_INTEGER
    if (pageA !== pageB) return pageA - pageB
    return (b.riskScore ?? 0) - (a.riskScore ?? 0)
  })

  const hasSummary = Boolean(headline || summary)
  const hasFindings = findings.length > 0

  return (
    <div data-testid="cr-analysis-layout" className={cn('flex flex-col gap-6 h-full', className)}>
      {hasSummary && (
        <SummaryCard
          title="Contract Review Summary"
          headline={headline}
          summary={summary}
          score={score}
          findings={sortedFindings}
          countLabel="finding"
        />
      )}
      {hasFindings ? (
        <FindingsList
          findings={sortedFindings}
          documentId={documentId}
          orgId={orgId}
          workspaceId={workspaceId}
          agentId={agentId}
          datasetKey={datasetKey}
          version={version}
          run={run}
          documentName={documentName}
        />
      ) : !hasSummary && (
        <div className="flex-1 flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50/50 min-h-[200px]">
          <div className="text-center px-6 py-8">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700">No Findings Detected</p>
            <p className="text-sm text-slate-500 mt-1 max-w-xs">
              Analysis complete. No issues were identified in this document.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
